import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
// import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import { setCookie } from "react-use-cookie";
import { Source } from "../hooks/useJobsQuery";
import getCountryCodeFromCountry from "./getCountryCodeFromCountry";
import getUniqueId from "./getUniqueId";

const COUNTRY = process.env.GATSBY_COUNTRY;
const DOMAIN = process.env.GATSBY_DOMAIN;

export type RegisterInputProps = {
  position: string;
  email: string;
  source: keyof typeof Source;
  firstname?: string;
  lastname?: string;
  location?: string;
  phone?: string;
  lscid?: string;
  clickid?: string;
  gclid?: string;
  compare?: boolean;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_term?: string;
  qrn_campaign?: string;
  qrn_content?: string;
  qrn_medium?: string;
  qrn_source?: string;
  qrn_term?: string;

  coRegPartner?: string;
  terms?: boolean;
  termsSms?: boolean;
  termsEmail?: boolean;
};

type RegisterPostBody = {
  countryCode: string;
  emailAddress: string;
  originDomain: string;
  userAgent: string;
  position: string;
  phone?: string;
  src: "path" | "serp" | "sms";
  location?: string;
  meta?: {
    lscid: string;
    clickid: string;
  };
  utmCampaign?: string;
  utmContent?: string;
  utmMedium?: string;
  utmSource?: string;
  utmTerm?: string;
  uniqueId?: string;
  gclid?: string;
  lang?: Navigator["language"];
};

interface RegisterPostBodyWithPartner extends RegisterPostBody {
  firstName: string;
  lastName: string;
  offer: string;
  jornayaId: string;
  originUrl: string;
}

const countryCode = getCountryCodeFromCountry();

export const cookieOptions = {
  days: 30,
  path: "/",
};

export function getPhone({ phone, countryCode }): string | undefined {
  if (phone && countryCode === "GB") {
    const phoneNumber = parsePhoneNumber(phone, countryCode);
    if (phoneNumber && isValidPhoneNumber(phoneNumber.number)) {
      phone = phoneNumber.number;
    }
  }
  return phone;
}

function injectJobCasePixel() {
  const img = document.createElement("img");
  img.src = `https://pcvn.jobcase.com/convert.jc?org=Nzc5Nz%3D`;
  img.width = 0;
  img.height = 0;
  document.body.appendChild(img);
}

export default async function coReg(data: RegisterInputProps) {
  const {
    email,
    position,
    gclid,
    lscid,
    clickid,
    location,
    source,
    utm_campaign,
    utm_content,
    utm_medium,
    utm_source,
    utm_term,
    qrn_campaign,
    qrn_content,
    qrn_medium,
    qrn_source,
    qrn_term,
    firstname,
    lastname,
    coRegPartner,
  } = data;

  const phone = getPhone({ phone: data.phone, countryCode });

  if (!position || !email) {
    const { source, ...rest } = data;
    if (source === Source.serp) {
      // should never happen as both these fields are required in SERP
      throw new Error("[co-reg] No position or email passed to API.");
    } else {
      // Also, should never happen either..
      // but on Path, thereit's possible to hit a DC step without position in the url,
      // and we've currently no way to ask for it. So we agreed to just skip to next step.
      return rest;
    }
  }

  const body: RegisterPostBody | RegisterPostBodyWithPartner = {
    countryCode,
    emailAddress: email,
    originDomain: `${COUNTRY}.${DOMAIN}`, // eg us.best-jobs-online.com
    userAgent: navigator.userAgent,
    uniqueId: getUniqueId(),
    position,
    src: source,
  };

  if (location) {
    // we should always have location, but just in case the fallback sniffer didn't work..
    body.location = location;
  }
  if (phone) {
    body.phone = phone;
  }
  if (lscid || clickid) {
    body.meta = {} as RegisterPostBody["meta"];
    body.meta.lscid = lscid;
    body.meta.clickid = clickid;
  }

  if (gclid) {
    body.gclid = gclid;
  }
  if (navigator.language) {
    body.lang = navigator.language;
  }

  if (utm_campaign || qrn_campaign) {
    body.utmCampaign = utm_campaign || qrn_campaign;
  }
  if (utm_content || qrn_content) {
    body.utmContent = utm_content || qrn_content;
  }
  if (utm_medium || qrn_medium) {
    body.utmMedium = utm_medium || qrn_medium;
  }
  if (utm_source || qrn_source) {
    body.utmSource = utm_source || qrn_source;
  }
  if (utm_term || qrn_term) {
    body.utmTerm = utm_term || qrn_term;
  }

  const jornayaId = (
    document.getElementById("leadid_token") as HTMLInputElement
  )?.value;
  if (coRegPartner && firstname && lastname && phone && jornayaId) {
    // emailAddress: "patti.cake@rabbit.com",
    // firstName: "Patty",
    // lastName: "Cake",
    // city: "New York",
    // state: "NJ",
    // zip: "1234567",
    // phone: "9802839797",
    // ipAddress: "192.168.1.2",
    // location: "New York",
    // originDomain: "www.google.com",
    // countryCode: "US",
    // offer:"FINUNITED",
    // originUrl:"https://best-jobs-online.com/some_path",
    // jornayaId: "37"

    body.jornayaId = jornayaId;
    body.firstName = firstname;
    body.lastName = lastname;
    body.offer = coRegPartner.toUpperCase();
    body.originUrl = window.location.href;
  }

  const response = await fetch(`https://api-v2.${DOMAIN}/register`, {
    method: "POST",
    body: JSON.stringify(body),
  });

  if (response.ok) {
    // reg: prefix is used to differentiate email/phone from other cookie
    // as they're the core co-register fields
    // we store cookies in useFormValues to stop PII data leaking
    // but without the reg: prefix
    if (email) {
      setCookie("reg:email", email, cookieOptions);
      // const json = await response.json();
      // if (json.qrnUserId && phone) {
      //   setCookie("reg:smsId", json.qrnUserId, { path: "/", days: 365 });
      // }
    }
    if (phone) setCookie("reg:phone", phone, cookieOptions);
    if (firstname) setCookie("firstname", firstname, cookieOptions);
    if (lastname) setCookie("lastname", lastname, cookieOptions);
    // pipe through original FormData
    // and any data from /register, eg hashed email ID for events logging
    const { source, ...rest } = data;

    if (source === "path" && COUNTRY === "us") {
      injectJobCasePixel();
    }

    return rest;
  }

  const json = await response.json();
  throw new Error(JSON.stringify(json.errors));
}
